import * as React from 'react';
import {
  DeletePlaceMutationFn,
  useDeletePlaceMutation,
} from '../generated/graphql';
import {
  BulkDeleteTarget,
  BulkDeleteOutcome,
  BulkDeleteResult,
  BulkDeleteState,
  buildBulkDeleteResult,
} from './PlaceListPage.logic';

// 選択した物件を順に削除し、中止や失敗があっても結果を確認できるようにする
export function usePlaceBulkDelete(
  onFinished: (result: BulkDeleteResult) => void
) {
  const [state, setState] = React.useState<BulkDeleteState>({ kind: 'idle' });
  const cancelRequestedRef = React.useRef(false);
  const pageOpenRef = React.useRef(true);
  const [deletePlace] = useDeletePlaceMutation();

  // 画面を閉じた後に、次の削除や表示の更新を始めないようにする
  React.useEffect(() => {
    return () => {
      pageOpenRef.current = false;
    };
  }, []);

  const openConfirm = (targets: BulkDeleteTarget[]): void => {
    setState({ kind: 'confirming', targets });
  };

  const cancelConfirm = (): void => {
    setState({ kind: 'idle' });
  };

  const startDeletion = async (targets: BulkDeleteTarget[]): Promise<void> => {
    const outcomes: BulkDeleteOutcome[] = [];

    cancelRequestedRef.current = false;

    for (const target of targets) {
      const canDeleteNext = !cancelRequestedRef.current && pageOpenRef.current;

      if (canDeleteNext) {
        setState({
          kind: 'deleting',
          targets,
          finishedCount: outcomes.length,
          cancelRequested: false,
        });
        outcomes.push(await deletePlaceOnce(deletePlace, target.id));
      }
    }

    if (pageOpenRef.current) {
      const result = buildBulkDeleteResult(
        targets,
        outcomes,
        cancelRequestedRef.current
      );

      if (
        result.failedTargets.length === 0 &&
        result.skippedTargets.length === 0
      ) {
        setState({ kind: 'idle' });
      } else {
        setState({ kind: 'finished', result });
      }

      onFinished(result);
    }
  };

  const requestCancel = (): void => {
    cancelRequestedRef.current = true;
    setState((currentState) => {
      if (currentState.kind === 'deleting') {
        return { ...currentState, cancelRequested: true };
      }
      return currentState;
    });
  };

  const closeResult = (): void => {
    setState({ kind: 'idle' });
  };

  return {
    state,
    openConfirm,
    cancelConfirm,
    startDeletion,
    requestCancel,
    closeResult,
  };
}

// 一件の失敗で残りの物件の削除まで止まらないよう、結果を共通の形にする
async function deletePlaceOnce(
  deletePlace: DeletePlaceMutationFn,
  placeId: number
): Promise<BulkDeleteOutcome> {
  try {
    const result = await deletePlace({ variables: { placeId } });

    if (result.data?.deletePlace) {
      return 'deleted';
    }
    return 'failed';
  } catch {
    return 'failed';
  }
}
